<template>
  <div class="ctx">
    <v-menu
      v-if="canSwitch"
      v-model="open"
      offset-y
      nudge-bottom="6"
      :close-on-content-click="false"
      content-class="ctx-menu"
      :min-width="device.isMobile ? 0 : 360"
      :max-width="device.isMobile ? '94vw' : 420"
      @input="onToggle"
    >
      <template #activator="{ on, attrs }">
        <button
          v-bind="attrs"
          type="button"
          class="ctx__chip"
          :aria-label="name + ' — ' + $t('nav_context_title')"
          aria-haspopup="dialog"
          :aria-expanded="String(open)"
          v-on="on"
        >
          <span class="ctx__avatar"><v-icon size="18" color="#c2410c">mdi-storefront-outline</v-icon></span>
          <span class="ctx__text">
            <b :title="name">{{ device.isMobile && pageTitle ? pageTitle : name }}</b>
            <span>{{ device.isMobile && pageTitle ? name : subtitleText }}</span>
          </span>
          <v-icon size="18" class="ctx__chev">mdi-chevron-down</v-icon>
        </button>
      </template>
      <div class="ctx-pop" role="dialog" :aria-label="$t('nav_context_title')" @keydown.esc="open = false">
        <div class="ctx-pop__cap">{{ $t("nav_sites") }}</div>
        <label class="ctx-pop__search">
          <v-icon size="16">mdi-magnify</v-icon>
          <input
            ref="search"
            v-model="query"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="ctx-list"
            autocomplete="off"
            :placeholder="$t('nav_context_search')"
            :aria-label="$t('nav_context_search')"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="pickActive"
          />
        </label>
        <div id="ctx-list" ref="list" class="ctx-pop__list" role="listbox">
          <div v-if="!rows.length" class="ctx-pop__empty">
            <b>{{ $t("nav_no_results", { q: query }) }}</b>
            <span>{{ $t("nav_context_hint") }}</span>
          </div>
          <template v-for="(row, i) in rows">
            <div v-if="row.heading" :key="'h-' + row.heading" class="ctx-pop__cap">{{ row.heading }}</div>
            <button
              v-else
              :key="row.site.id"
              type="button"
              role="option"
              class="ctx-pop__row"
              :class="{ 'is-on': row.index === cursor }"
              :aria-selected="String(isCurrent(row.site))"
              :data-row="row.index"
              @click="pick(row.site)"
              @mouseenter="cursor = row.index"
            >
              <span class="ctx-pop__ri"><v-icon size="16" color="#c2410c">mdi-storefront-outline</v-icon></span>
              <span class="ctx-pop__rt">
                <b>{{ toLatin(row.site.name) }}</b>
                <span v-if="siteSub(row.site)">{{ siteSub(row.site) }}</span>
              </span>
              <v-icon v-if="isCurrent(row.site)" size="18" color="#c2410c">mdi-check</v-icon>
            </button>
          </template>
        </div>
      </div>
    </v-menu>
    <div v-else class="ctx__chip ctx__chip--static">
      <span class="ctx__avatar"><v-icon size="18" color="#c2410c">mdi-domain</v-icon></span>
      <span class="ctx__text">
        <b :title="name">{{ device.isMobile && pageTitle ? pageTitle : name }}</b>
        <span v-if="(device.isMobile && pageTitle) || subtitleText">{{
          device.isMobile && pageTitle ? name : subtitleText
        }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import { matchesText } from "@/utils/searchText";
import { navState } from "@/utils/navPrefs";
import { selectSite } from "@/utils/siteContext";

export default {
  name: "ContextSwitcher",
  props: {
    subtitle: { type: String, default: "" },
    pageTitle: { type: String, default: "" },
  },
  data: () => ({ open: false, query: "", cursor: 0 }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    company() {
      return this.$store.state.company;
    },
    site() {
      return this.$store.state.selectedRestaurant;
    },
    websites() {
      return this.user?.restaurants || [];
    },
    canSwitch() {
      return this.websites.length > 1 && this.user?.type !== "admin_delivery";
    },
    // Naziv u traci, isti izbor kao što ga je pravio stari naslov.
    name() {
      const user = this.user;
      if (user.type === "supplier" && user.suppliers && user.suppliers[0]) {
        return this.toLatin(user.suppliers[0].name);
      }
      if (user.type === "admin_delivery") {
        return this.toLatin(
          this.company?.name ||
            (user.companies || []).find((c) => c && c.is_delivery)?.name ||
            user.companies?.[0]?.name ||
            ""
        );
      }
      return this.toLatin(this.site?.name || this.company?.name || "");
    },
    subtitleText() {
      return this.canSwitch ? this.$t("nav_sites_total", { n: this.websites.length }) : this.subtitle;
    },
    // Lista sa naslovima "Nedavno" i "Svi"; rows[].index je redni broj pogotka (za strelice).
    rows() {
      const found = this.websites.filter((w) => matchesText(`${w.name} ${w.city || ""}`, this.query));
      const out = [];
      let index = 0;
      const recent = this.query.trim()
        ? []
        : navState.recentSites.map((id) => found.find((w) => w.id === id)).filter(Boolean);
      if (recent.length) {
        out.push({ heading: this.$t("nav_recent") });
        recent.forEach((site) => out.push({ site, index: index++ }));
        out.push({ heading: this.$t("nav_all") });
      }
      found
        .filter((w) => !recent.includes(w))
        .slice(0, 80)
        .forEach((site) => out.push({ site, index: index++ }));
      return out;
    },
    flatRows() {
      return this.rows.filter((r) => r.site);
    },
  },
  watch: {
    // Prvi izbor: sačuvan restoran, inače prvi (kao stari SitesDropdown).
    websites: {
      handler(list) {
        if (list.length > 0 && !this.site) {
          const savedId = localStorage.getItem("restorani-selected-restaurant");
          const saved = savedId && list.find((w) => String(w.id) === savedId);
          selectSite(this.$store, saved || list[0]);
        }
      },
      immediate: true,
    },
    query() {
      this.cursor = 0;
    },
  },
  methods: {
    siteSub(site) {
      return this.toLatin(site.city || site.address || "");
    },
    isCurrent(site) {
      return this.site && this.site.id === site.id;
    },
    onToggle(isOpen) {
      if (isOpen) {
        this.query = "";
        this.cursor = 0;
        this.$nextTick(() => setTimeout(() => this.$refs.search && this.$refs.search.focus(), 30));
      }
    },
    move(step) {
      const n = this.flatRows.length;
      if (!n) return;
      this.cursor = (this.cursor + step + n) % n;
      this.$nextTick(() => {
        const el = this.$refs.list && this.$refs.list.querySelector(`[data-row="${this.cursor}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    },
    pickActive() {
      const row = this.flatRows[this.cursor];
      if (row) this.pick(row.site);
    },
    pick(site) {
      selectSite(this.$store, site);
      this.open = false;
    },
  },
};
</script>

<style lang="scss">
.ctx {
  min-width: 0;
  display: flex;
  &__chip {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 42px;
    max-width: 360px;
    min-width: 0;
    padding: 0 10px 0 8px;
    border: 1px solid transparent;
    border-radius: 12px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    &:hover,
    &[aria-expanded="true"] {
      background: #f7f1ed;
      border-color: #f0e2d9;
    }
    &--static {
      cursor: default;
      &:hover {
        background: transparent;
        border-color: transparent;
      }
    }
    &:focus-visible {
      outline: 2px solid #1d4ed8;
      outline-offset: 1px;
    }
  }
  &__avatar {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: #fff1ea;
    display: grid;
    place-items: center;
    flex: none;
  }
  &__text {
    display: grid;
    min-width: 0;
    line-height: 1.2;
    b {
      font-size: 0.95rem;
      font-weight: 700;
      color: #1c1410;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    span {
      font-size: 0.72rem;
      color: #6b5a50;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__chev {
    color: #6b5a50 !important;
  }
}
.ctx-menu {
  border-radius: 16px !important;
  border: 1px solid #e6d9cf;
  box-shadow: 0 18px 44px -12px rgba(60, 30, 10, 0.28), 0 2px 6px rgba(60, 30, 10, 0.08) !important;
  background: #fff;
}
.ctx-pop {
  &__cap {
    padding: 12px 14px 6px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #756559;
  }
  &__search {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 10px 8px;
    padding: 0 10px;
    height: 38px;
    border: 1px solid #e6d9cf;
    border-radius: 10px;
    color: #6b5a50;
    &:focus-within {
      border-color: #1d4ed8;
      box-shadow: 0 0 0 2px #dbe6ff;
    }
    input {
      border: 0;
      outline: 0;
      flex: 1;
      min-width: 0;
      height: 100%;
      background: transparent;
      color: #1c1410;
    }
  }
  &__list {
    max-height: 300px;
    overflow-y: auto;
    padding-bottom: 6px;
  }
  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 44px;
    padding: 6px 14px;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    &.is-on,
    &:hover {
      background: #f7f1ed;
    }
  }
  &__ri {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: #fff1ea;
    display: grid;
    place-items: center;
    flex: none;
  }
  &__rt {
    display: grid;
    min-width: 0;
    flex: 1;
    line-height: 1.25;
    b {
      font-weight: 600;
      font-size: 0.88rem;
      color: #1c1410;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    span {
      font-size: 0.74rem;
      color: #6b5a50;
    }
  }
  &__empty {
    display: grid;
    gap: 6px;
    justify-items: center;
    text-align: center;
    padding: 18px 16px;
    font-size: 0.86rem;
    color: #6b5a50;
    b {
      color: #1c1410;
    }
  }
}
</style>
